import { useState } from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { httpsCallable } from 'firebase/functions';

import { useAuth } from '../../lib/auth';
import { functions } from '../../lib/firebase';
import { getEventId } from '../../event/paths';
import { DOB_MESSAGES, checkDateOfBirth, dateOfBirthBounds, needsDateOfBirthFix, pickerValueToIso } from '../../utils/dateOfBirth';
import { serverMessageOr } from '../../utils/authErrors';

// One-time ask for hosts whose saved date of birth is unreadable (the old register form saved
// "Invalid Date"). Shows on the host's OWN profile only, never while viewing as someone else.
// "Not now" hides it for this browser session; it comes back next time until it is fixed.
// Saving closes it on its own: the live profile updates and the check below turns false.

const dismissKey = (uid) => `dobPromptDismissed:${getEventId()}:${uid}`;

function readDismissed(uid) {
  try {
    return window.sessionStorage.getItem(dismissKey(uid)) === '1';
  } catch {
    return false;
  }
}

export default function DateOfBirthPrompt() {
  const { user, realProfile, impersonatedProfile } = useAuth();
  const uid = user?.uid || '';
  const [dismissed, setDismissed] = useState(() => (uid ? readDismissed(uid) : false));
  const [value, setValue] = useState(null);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const open = Boolean(uid && realProfile && !impersonatedProfile && !dismissed && needsDateOfBirthFix(realProfile.dateOfBirth));
  if (!open) return null;

  const check = checkDateOfBirth(pickerValueToIso(value));
  // A half-typed date reaches us as empty (the picker only reports whole dates), so "missing"
  // asks for the full date too rather than "Enter your date of birth" over a part-filled field.
  const fieldError =
    touched && !check.ok ? (check.code === 'UNDER_AGE' || check.code === 'TOO_OLD' ? check.message : DOB_MESSAGES.INVALID) : '';

  const notNow = () => {
    try {
      window.sessionStorage.setItem(dismissKey(uid), '1');
    } catch {
      // private window: it just shows again next time
    }
    setDismissed(true);
  };

  const save = async () => {
    setTouched(true);
    if (!check.ok) return;
    setSaving(true);
    setError('');
    try {
      await httpsCallable(functions, 'updateMyDateOfBirth')({ eventId: getEventId(), dateOfBirth: check.value });
    } catch (err) {
      setError(serverMessageOr(err, "We couldn't save that. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open maxWidth="xs" fullWidth onClose={saving ? undefined : notNow}>
      <DialogTitle>Add your date of birth</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Your date of birth didn&apos;t save properly when you signed up. Please add it again so your profile is complete.
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Date of birth"
            format="DD/MM/YYYY"
            value={value}
            onChange={(next) => {
              setValue(next);
              setError('');
            }}
            openTo="year"
            views={['year', 'month', 'day']}
            minDate={dayjs(dateOfBirthBounds().min)}
            maxDate={dayjs(dateOfBirthBounds().max)}
            slotProps={{
              textField: {
                id: 'dob-prompt',
                fullWidth: true,
                placeholder: 'DD/MM/YYYY',
                onBlur: () => setTouched(true),
                error: Boolean(fieldError),
                helperText: fieldError || ' '
              }
            }}
          />
        </LocalizationProvider>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={notNow} disabled={saving} color="secondary">
          Not now
        </Button>
        <Button
          onClick={save}
          disabled={saving}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
